import { DeltaPositionsType } from "template-edit/containers/image-preview"
import { ClipMaskObject } from "./edit-clip-mask-modal"
import { isJson } from "app/helpers/other"
import { PathPoint } from "shape-entities/types/PathShape"
import { BrushStrokeType } from "shape-entities/components/brush-svg"

const { min, max, abs, sin, cos, PI, round, atan2, ceil, floor } = Math

export function getDeltaSizeAndPositionFromDeltaPositions(deltaPositions: DeltaPositionsType, shapeWidth: number, shapeHeight: number): { deltaWidth: number, deltaHeight: number, deltaXPos: number, deltaYPos: number } {
    if (deltaPositions) {
        const x1_orig = -.5 * shapeWidth
        const x2_orig = .5 * shapeWidth
        const y1_orig = -.5 * shapeHeight
        const y2_orig = .5 * shapeHeight

        const x1 = x1_orig + deltaPositions.x1
        const x2 = x2_orig + deltaPositions.x2
        const y1 = y1_orig + deltaPositions.y1
        const y2 = y2_orig + deltaPositions.y2
                
        const newWidth = abs(x2 - x1)
        const newHeight = abs(y2 - y1)

        const deltaWidth = newWidth - shapeWidth
        const deltaHeight = newHeight - shapeHeight

        return {
            deltaWidth: deltaWidth,
            deltaHeight: deltaHeight,
            deltaXPos: deltaPositions.x1 + .5 * deltaWidth,
            deltaYPos: deltaPositions.y1 + .5 * deltaHeight,
        }
    }
    
    return {
            deltaWidth: 0,
            deltaHeight: 0,
            deltaXPos: 0,
            deltaYPos: 0,
        }
}

export function recalcClipMaskSizeAndPositions(deltaPositions: DeltaPositionsType, shapeWidth: number, shapeHeight: number, clipMaskObjectsString: string, isSelected: boolean): ClipMaskObject[] {
    const clipMaskObjects = clipMaskObjectsString && clipMaskObjectsString.length > 0 && isJson(clipMaskObjectsString) ? JSON.parse(clipMaskObjectsString) : null as ClipMaskObject[]

    if (!isSelected) {
        return clipMaskObjects
    }

    const d = getDeltaSizeAndPositionFromDeltaPositions(deltaPositions, shapeWidth, shapeHeight)
    if (clipMaskObjects && !(deltaPositions.x1 === 0 && deltaPositions.x2 === 0 && deltaPositions.y1 === 0 && deltaPositions.y2 === 0)) {
        const newWidth = shapeWidth + d.deltaWidth
        const newHeight = shapeHeight + d.deltaHeight
        
        const scaleX = newWidth / shapeWidth
        const scaleY = newHeight / shapeHeight

        let updatedMaskObjects = []
        clipMaskObjects.forEach((maskObject: ClipMaskObject) => {
            updatedMaskObjects.push({
                ...maskObject,
                type: maskObject.type === 'circle' && scaleX !== scaleY ? 'ellipse' : maskObject.type,
                x: round(maskObject.x*scaleX),
                y: round(maskObject.y*scaleY),
                points: scalePoints(maskObject.points, scaleX, scaleY),
                width:  floor(maskObject.width*scaleX),
                height: floor(maskObject.height*scaleY),
            })            
        })

        return updatedMaskObjects
    }

    return clipMaskObjects
}

export function scaleBrushStrokes(brushStrokes: BrushStrokeType[] | string, scaleX: number, scaleY: number) : BrushStrokeType[] | string {    
    if (!brushStrokes || (scaleX === 1 && scaleY === 1)) {
        return brushStrokes
    }

    const isString = typeof brushStrokes === 'string'

    const parsed = isString && isJson(brushStrokes) ? JSON.parse(brushStrokes) : brushStrokes
    if (!Array.isArray(parsed)) {
        return null
    }

    const avgScale = (scaleX+scaleY)/2

    const rescaled = parsed.map(brushStroke => {
        return {
            ...brushStroke,
            points: brushStroke.points.map(point => {
                return {
                    x: round(point.x * scaleX),
                    y: round(point.y * scaleY)
                }
            }),
            radius: round(brushStroke.radius * avgScale),
        }
    })

    return isString ? JSON.stringify(rescaled) : rescaled
}

export function scalePoints(points: PathPoint[], scaleX: number, scaleY: number) {
    if (!points || !points.length) {
        return points
    }

    let updatedPoints = []   
    updatedPoints = JSON.parse(JSON.stringify(points))
    updatedPoints.forEach((point, key) => {
        updatedPoints[key].x = round(updatedPoints[key].x * scaleX)
        updatedPoints[key].y = round(updatedPoints[key].y * scaleY)

        if (typeof updatedPoints[key].cx !== "undefined") {
            updatedPoints[key].cx = round(updatedPoints[key].cx * scaleX)
            updatedPoints[key].cy = round(updatedPoints[key].cy * scaleY)
        }

        if (typeof updatedPoints[key].cx2 !== "undefined") {
            updatedPoints[key].cx2 = round(updatedPoints[key].cx2 * scaleX)
            updatedPoints[key].cy2 = round(updatedPoints[key].cy2 * scaleY)
        }
    })
    
    return updatedPoints
}

export function getDirectionFromDeltaPositions(deltaXpos: number, deltaYpos: number): string {
    let direction = 'none'
    const alpha = (atan2(deltaYpos, deltaXpos) * 180 / PI + 90 + 360) % 360
    const alphaStep = 360 / 8
    const alphaHalfStep = alphaStep / 2
        
    if (alpha > 360 - alphaHalfStep && alpha <= alphaStep - alphaHalfStep) {
        direction = 'N'
    } else if (alpha > alphaStep - alphaHalfStep && alpha <= 2 * alphaStep - alphaHalfStep) {
        direction = 'NE'
    } else if (alpha > 2 * alphaStep - alphaHalfStep && alpha <= 3 * alphaStep - alphaHalfStep) {
        direction = 'E'
    } else if (alpha > 3 * alphaStep - alphaHalfStep && alpha <= 4 * alphaStep - alphaHalfStep) {
        direction = 'SE'
    } else if (alpha > 4 * alphaStep - alphaHalfStep && alpha <= 5 * alphaStep - alphaHalfStep) {
        direction = 'S'
    } else if (alpha > 5 * alphaStep - alphaHalfStep && alpha <= 6 * alphaStep - alphaHalfStep) {
        direction = 'SW'
    } else if (alpha > 6 * alphaStep - alphaHalfStep && alpha <= 7 * alphaStep - alphaHalfStep) {
        direction = 'W'
    } else if (alpha > 7 * alphaStep - alphaHalfStep && alpha <= 8 * alphaStep - alphaHalfStep) {
        direction = 'NW'
    }
        
    return direction
}